// @flow
import * as React from 'react'
import loadGoogleMaps from 'load_google_maps'
import type { GoogleMapsAPI, GoogleMap } from 'types/google_maps'
import MapEmbedPresentation from './Presentation'

import './MapEmbed.scss'

const LOADING_STATE = {
  loading: 'loading',
  failed: 'failed',
  ok: 'ok',
}

type LoadingState = $Keys<typeof LOADING_STATE>

export type MapParams = {
  centerLat: number,
  centerLng: number,
  zoom: number,
}

type Props = {
  centerLat: number,
  centerLng: number,
  zoom: number,
  fitBounds: boolean,
  shouldUpdateMapParams: (prevProps: MapParams, currProps: MapParams) => boolean,
  fullscreenControl: boolean,
  showStreetViewControl: boolean,
  showZoomControl: boolean,
  scrollwheel: boolean,
  customButton?: {
    ctaText: string,
    onCtaClick: (map: GoogleMap) => void,
  },
  children?: React.Node,
}

type State = {
  loadingState: LoadingState,
  gMaps?: GoogleMapsAPI,
  map?: GoogleMap,
}

export default class Map extends React.Component<Props, State> {
  mapEl: HTMLElement

  static defaultProps = {
    centerLat: 28.4516899,
    centerLng: -81.4463165, // tangelo park project
    zoom: 15,
    fitBounds: false,
    shouldUpdateMapParams: () => true,
    scrollwheel: false,
    fullscreenControl: false,
    showStreetViewControl: true,
    showZoomControl: true,
  }

  constructor(props: Props) {
    super(props)

    this.state = {
      loadingState: LOADING_STATE.loading,
      gMaps: undefined,
    }
  }

  componentDidMount = () => {
    loadGoogleMaps().then(this.handleGoogleMapsLoadSucceeded).catch(this.handleGoogleMapsLoadFailed)
  }

  render = () => {
    switch (this.state.loadingState) {
      case LOADING_STATE.loading:
        return this.renderLoading()

      case LOADING_STATE.failed:
        return this.renderFailure()

      case LOADING_STATE.ok:
        return this.renderMap()

      default:
        throw new Error('unknown loading state')
    }
  }

  renderLoading = () => {
    return <div className="MapEmbed MapEmbed--loading" />
  }

  renderFailure = () => {
    const errorText = 'Sorry, we are having trouble loading the map right now.'
    return (
      <div className="MapEmbed MapEmbed--initializationFailed">
        <div className="MapEmbed-errorText">{errorText}</div>
      </div>
    )
  }

  renderMap = () => {
    return (
      <MapEmbedPresentation
        gMaps={this.state.gMaps}
        centerLat={this.props.centerLat}
        centerLng={this.props.centerLng}
        zoom={this.props.zoom}
        fitBounds={this.props.fitBounds}
        shouldUpdateMapParams={this.props.shouldUpdateMapParams}
        scrollwheel={this.props.scrollwheel}
        fullscreenControl={this.props.fullscreenControl}
        showStreetViewControl={this.props.showStreetViewControl}
        showZoomControl={this.props.showZoomControl}
        customButton={this.props.customButton}
      >
        {this.props.children}
      </MapEmbedPresentation>
    )
  }

  handleGoogleMapsLoadSucceeded = (gMaps: GoogleMapsAPI) => {
    // if the google maps api isn't returned, treat as failure
    if (!gMaps) return this.handleGoogleMapsLoadFailed()

    this.setState({ loadingState: LOADING_STATE.ok, gMaps })
  }

  handleGoogleMapsLoadFailed = () => {
    this.setState({ loadingState: LOADING_STATE.failed })
  }
}
